import { closestCorners, DndContext, type DragEndEvent } from "@dnd-kit/core";
import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
  type UIEvent,
} from "react";
import { useBoardSensors } from "~/components/data-views/boardDnd";
import { PlanningBoardColumnHeader } from "~/components/data-views/PlanningBoardColumnHeader";
import { toastManager } from "~/components/ui/toast";
import { ProjectTaskCard } from "./ProjectTaskCard";
import { canQuickCreateInWorkflowGroup } from "./projectTaskCreateOptions";
import {
  MilestoneLaneSection,
  shouldExpandMilestoneLane,
} from "./ProjectTaskMilestoneSwimlaneRenderers";
import { taskMilestoneGroups, tasksByMilestoneGroup } from "./taskMilestoneGroups";
import { resolveTaskBoardDrag, validateTaskBoardDrag } from "./taskMilestoneSwimlaneDnd";
import { normalizeTaskStatus, type ProjectTaskBoardViewProps } from "./taskViewTypes";
import { milestoneStatusColumns } from "./taskMilestoneWorkflowColumns";
import { workflowGroupForTask } from "./taskWorkflowGroups";

type MilestoneBoardProps = Omit<ProjectTaskBoardViewProps, "groupBy" | "onSetStatus">;

function updateActiveLane(
  viewport: HTMLDivElement,
  setActiveLaneKey: Dispatch<SetStateAction<string | null>>,
): void {
  const statusRail = viewport.querySelector<HTMLElement>("[data-task-milestone-status-rail]");
  const stickyThreshold =
    statusRail?.getBoundingClientRect().bottom ?? viewport.getBoundingClientRect().top;
  const headers = viewport.querySelectorAll<HTMLElement>("[data-task-milestone-sticky-header]");
  if (
    headers.length > 0 &&
    viewport.scrollTop + viewport.clientHeight >= viewport.scrollHeight - 1
  ) {
    const trailingKey = headers.item(headers.length - 1).dataset["taskMilestoneStickyHeader"];
    if (trailingKey != null)
      setActiveLaneKey((current) => (current === trailingKey ? current : trailingKey));
    return;
  }
  let nearestKey: string | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;
  headers.forEach((header) => {
    const laneKey = header.dataset["taskMilestoneStickyHeader"];
    if (laneKey == null) return;
    const distance = Math.abs(header.getBoundingClientRect().top - stickyThreshold);
    if (distance <= nearestDistance) {
      nearestDistance = distance;
      nearestKey = laneKey;
    }
  });
  if (nearestKey != null)
    setActiveLaneKey((current) => (current === nearestKey ? current : nearestKey));
}

function milestoneColumnCreateAction(
  props: MilestoneBoardProps,
  columnStatus: string,
  activeGroup: ReturnType<typeof taskMilestoneGroups>[number] | null,
): (() => void) | undefined {
  if (
    !canQuickCreateInWorkflowGroup(props.workflow, columnStatus, props.mutationDisabled ?? false)
  ) {
    return undefined;
  }
  if (activeGroup != null && props.onCreateInLane != null) {
    return () => {
      const stage = props.workflow?.stages.find((candidate) => candidate.id === columnStatus);
      props.onCreateInLane?.({
        status: stage?.category ?? normalizeTaskStatus(columnStatus),
        milestoneId: activeGroup.milestoneId,
        ...(stage != null ? { workflowStageId: stage.id } : {}),
      });
    };
  }
  return props.onCreateInGroup != null ? () => props.onCreateInGroup?.(columnStatus) : undefined;
}

export function ProjectTaskMilestoneSwimlaneBoard(props: MilestoneBoardProps) {
  const sensors = useBoardSensors();
  const viewportRef = useRef<HTMLDivElement>(null);
  const milestones = props.milestones ?? [];
  const groups = taskMilestoneGroups(milestones);
  const statusColumns = milestoneStatusColumns(props.tasks, props.workflow);
  const milestoneById = new Map(milestones.map((milestone) => [milestone.id, milestone]));
  const tasksByLane = tasksByMilestoneGroup(props.tasks, groups);
  const [laneExpansionOverrides, setLaneExpansionOverrides] = useState<
    ReadonlyMap<string, boolean>
  >(() => new Map());
  const [activeLaneKey, setActiveLaneKey] = useState<string | null>(null);
  // Drives the collapsed-lane drop strip (CMMD-2209): a lane that renders collapsed has no
  // droppable of its own, and the pointer is busy for the whole gesture, so the targets have
  // to exist for the duration of the drag rather than on demand.
  const [dragActive, setDragActive] = useState(false);
  const placementByCardId = new Map(
    groups.flatMap((group) =>
      (tasksByLane.get(group.key) ?? []).map((task) => [
        String(task.id),
        {
          laneId: group.key,
          columnId: workflowGroupForTask(task, props.workflow).key,
        },
      ]),
    ),
  );
  const columnCount = Math.max(statusColumns.length, 1);
  const gridWidth = columnCount * 260 + Math.max(columnCount - 1, 0) * 12;
  const laneLayoutKey = groups
    .map((group) => {
      const laneTasks = tasksByLane.get(group.key) ?? [];
      const defaultExpanded = shouldExpandMilestoneLane(
        laneTasks,
        props.reorderingDisabled ?? false,
      );
      const placements = laneTasks
        .map((task) => `${task.id}:${workflowGroupForTask(task, props.workflow).key}`)
        .join(",");
      return `${group.key}:${laneExpansionOverrides.get(group.key) ?? defaultExpanded}:${placements}`;
    })
    .join("|");

  const toggleLane = (laneId: string, defaultExpanded: boolean) => {
    setLaneExpansionOverrides((current) => {
      const next = new Map(current);
      next.set(laneId, !(current.get(laneId) ?? defaultExpanded));
      return next;
    });
  };
  const handleDragEnd = (event: DragEndEvent) => {
    setDragActive(false);
    if (props.mutationDisabled ?? false) return;
    const workflowCategoryByStageId = new Map(
      props.workflow?.stages.map((stage) => [stage.id, stage.category] as const) ?? [],
    );
    const drag = resolveTaskBoardDrag(
      String(event.active.id),
      event.over != null ? String(event.over.id) : null,
      placementByCardId,
      {
        preservePosition: !(props.reorderingDisabled ?? false),
        workflowCategoryByStageId,
      },
    );
    const validation = validateTaskBoardDrag(drag, props.tasks, props.workflow);
    if (validation.kind === "rejected") {
      toastManager.add({
        type: "warning",
        title: "Transition not allowed",
        description: "Move this task through an allowed workflow stage first.",
      });
      return;
    }
    if (validation.kind === "allowed") {
      props.onMoveTask(validation.drag.taskId, validation.drag.move);
    }
  };
  const handleBoardScroll = (event: UIEvent<HTMLDivElement>) => {
    updateActiveLane(event.currentTarget, setActiveLaneKey);
  };
  useEffect(() => {
    const viewport = viewportRef.current;
    if (viewport == null) return;
    const frame = requestAnimationFrame(() => updateActiveLane(viewport, setActiveLaneKey));
    if (typeof ResizeObserver === "undefined") return () => cancelAnimationFrame(frame);
    const observer = new ResizeObserver(() => updateActiveLane(viewport, setActiveLaneKey));
    observer.observe(viewport);
    viewport
      .querySelectorAll<HTMLElement>("[data-task-milestone-swimlane]")
      .forEach((lane) => observer.observe(lane));
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [laneLayoutKey]);
  const activeGroup = groups.find((group) => group.key === activeLaneKey) ?? groups[0] ?? null;
  const renderCard = (task: CmmdBoardTask) => (
    <ProjectTaskCard
      task={task}
      threadIndex={props.threadIndex}
      pending={props.pendingTaskIds.has(task.id)}
      hasConversation={props.conversationTaskIds?.has(task.id) ?? false}
      onOpenTask={() => props.onOpenTask(task.id)}
      onOpenThread={props.onOpenThread}
      workItemPrefix={props.workItemPrefix}
    />
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={() => setDragActive(true)}
      onDragCancel={() => setDragActive(false)}
      onDragEnd={handleDragEnd}
    >
      <div
        data-task-milestone-board-scroll
        ref={viewportRef}
        onScroll={handleBoardScroll}
        data-task-board
        className="min-h-0 flex-1 overflow-auto overscroll-contain bg-[var(--task-board-canvas)] [--task-board-status-rail-offset:calc(48px+1.25rem)] md:[--task-board-status-rail-offset:3rem]"
      >
        <div className="w-full space-y-[8px] p-[12px]" style={{ minWidth: `${gridWidth + 24}px` }}>
          <div
            data-task-milestone-status-rail
            className="sticky top-0 z-30 flex gap-[12px] bg-[var(--task-board-canvas)] pt-1"
          >
            {statusColumns.map((column) => (
              <div
                key={column.status}
                className="w-[260px] shrink-0 rounded-xl bg-[var(--task-board-column)]"
              >
                <PlanningBoardColumnHeader
                  label={column.label}
                  count={column.tasks.length}
                  leading={column.leading}
                  onCreate={milestoneColumnCreateAction(props, column.status, activeGroup)}
                  createLabel={
                    activeGroup != null && props.onCreateInLane != null
                      ? `Add task to ${activeGroup.label} in ${column.label}`
                      : `Add task in ${column.label}`
                  }
                />
              </div>
            ))}
          </div>
          {groups.map((group) => (
            <MilestoneLaneSection
              key={group.key}
              group={group}
              laneTasks={tasksByLane.get(group.key) ?? []}
              milestone={
                group.milestoneId != null ? (milestoneById.get(group.milestoneId) ?? null) : null
              }
              statusColumns={statusColumns}
              expansionOverride={laneExpansionOverrides.get(group.key)}
              searchActive={props.reorderingDisabled ?? false}
              renderCard={renderCard}
              onOpenTask={props.onOpenTask}
              onToggle={toggleLane}
              onManageMilestone={props.onManageMilestone}
              mutationDisabled={props.mutationDisabled ?? false}
              dragActive={dragActive}
            />
          ))}
        </div>
      </div>
    </DndContext>
  );
}
